// Optional, subtle UI sound cues synthesised with WebAudio — no audio files, off by default.
// Self-gating: every cue checks the persisted preference, so callers never need to thread state.

type Cue = "send" | "receive" | "open";
const KEY = "voss-sound";

let ctx: AudioContext | null = null;

const enabled = (): boolean => (typeof localStorage !== "undefined" ? localStorage.getItem(KEY) === "true" : false);

const audioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx ??= new Ctor();
  return ctx;
};

const TONES: Record<Cue, { freq: number; dur: number; gain: number }> = {
  send: { freq: 523.25, dur: 0.07, gain: 0.04 },
  receive: { freq: 392.0, dur: 0.09, gain: 0.035 },
  open: { freq: 659.25, dur: 0.05, gain: 0.03 },
};

export const soundEnabled = enabled;
export const setSoundEnabled = (on: boolean): void => localStorage.setItem(KEY, String(on));

/** Play a soft sine blip if sounds are enabled. Fails silently where WebAudio is unavailable. */
export function playCue(cue: Cue): void {
  if (!enabled()) return;
  const ac = audioContext();
  if (!ac) return;
  try {
    const { freq, dur, gain } = TONES[cue];
    const osc = ac.createOscillator();
    const vol = ac.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    vol.gain.setValueAtTime(0, ac.currentTime);
    vol.gain.linearRampToValueAtTime(gain, ac.currentTime + 0.01);
    vol.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
    osc.connect(vol).connect(ac.destination);
    osc.start();
    osc.stop(ac.currentTime + dur);
  } catch {
    // ignore — audio is a non-essential nicety
  }
}
